import {
  StandardSizeTable,
  StandardSizeDefinition,
} from "../features/rapidstor/entities/StandardSize";

/**
 * Default standard size table used when a facility has no specific configuration.
 * Covers a typical range of self-storage unit sizes in m².
 *
 * To customise per facility, add an entry to `facilityStandardSizes` below
 * keyed by the Rapidstor location code (e.g. "L008").
 */
const defaultUnitSizes: StandardSizeDefinition[] = [
  { label: "Locker", minArea: 0.1, maxArea: 1.59 },
  { label: "Wardrobe", minArea: 1.6, maxArea: 3.19 },
  { label: "Walk In Robe", minArea: 3.2, maxArea: 4.29 },
  { label: "Quarter Garage", minArea: 4.3, maxArea: 7.99 },
  { label: "Half Garage", minArea: 8, maxArea: 12.59 },
  { label: "Three Quarter Garage", minArea: 12.6, maxArea: 16.99 },
  { label: "Single Garage", minArea: 17, maxArea: 20.09 },
  { label: "Extra Large", minArea: 20.1, maxArea: 99 },
];

const defaultTable: StandardSizeTable = {
  units: defaultUnitSizes,
};

/**
 * Per-facility standard size tables, keyed by Rapidstor location code.
 * Facilities not listed here fall back to `defaultTable`.
 *
 * This is now used as a fallback when WordPress REST API is not available.
 */
const facilityStandardSizes: Record<string, Partial<StandardSizeTable>> = {};

/**
 * Cache for fetched standard sizes from WordPress
 */
const standardSizesCache: Record<string, StandardSizeTable> = {};

/**
 * Resolve the base URL for the WordPress Standard Sizes API.
 *
 * Allows overriding via VITE_WORDPRESS_API_BASE while preserving the
 * existing default of "/wp-json/keepsafe/v1".
 *
 * Examples accepted:
 * - "/wp-json/keepsafe/v1"
 * - "/wp-json/keepsafe/v1/"
 * - "https://example.com/wp-json/keepsafe/v1"
 * - "https://example.com/wp-json/keepsafe/v1/"
 */
function getWordPressStandardSizesBaseURL(): string {
  const envBase = (import.meta as any).env?.VITE_WORDPRESS_API_BASE as string | undefined;

  const base = envBase && envBase.trim().length > 0 ? envBase : "";

  // Normalise: remove trailing slashes to avoid double slashes
  return base.replace(/\/+$/, "");
}

function buildStandardSizesUrl(locationCode: string): string {
  const base = getWordPressStandardSizesBaseURL();
  return `${base}/wp-json/keepsafe/v1/standard-sizes/${locationCode}`;
}

/**
 * Fetch standard sizes from WordPress REST API
 * Falls back to hardcoded config if API is not available
 */
async function fetchStandardSizesFromWordPress(
  locationCode: string
): Promise<StandardSizeTable | null> {
  try {
    // Try to fetch from WordPress REST API
    const response = await fetch(buildStandardSizesUrl(locationCode));

    if (response.ok) {
      const data = await response.json();

      // Transform WordPress format to our format
      // WordPress stores: [{ label, min_area, max_area }]
      // We need: [{ label, minArea, maxArea }]
      const units: StandardSizeDefinition[] = (data.units || []).map((unit: any) => ({
        label: unit.label,
        minArea: unit.min_area,
        maxArea: unit.max_area,
        ...(unit.type ? { type: unit.type } : {}),
      }));

      return { units };
    }
  } catch (error) {
    console.warn(
      `Failed to fetch standard sizes from WordPress for ${locationCode}:`,
      error
    );
  }

  return null;
}

/**
 * Returns the standard size table for a given facility.
 * First tries to fetch from WordPress REST API, then falls back to hardcoded config.
 */
export async function getStandardSizeTableAsync(
  locationCode: string
): Promise<StandardSizeTable> {
  // Check cache first
  if (standardSizesCache[locationCode]) {
    return standardSizesCache[locationCode];
  }

  // Try WordPress API
  const wpSizes = await fetchStandardSizesFromWordPress(locationCode);
  if (wpSizes && wpSizes.units.length > 0) {
    standardSizesCache[locationCode] = wpSizes;
    return wpSizes;
  }

  // Fallback to hardcoded config
  const override = facilityStandardSizes[locationCode];
  const table = override ? { units: override.units ?? defaultTable.units } : defaultTable;

  standardSizesCache[locationCode] = table;
  return table;
}

/**
 * Synchronous version for backward compatibility
 * Returns the standard size table for a given facility.
 * Merges facility-specific overrides with the default table.
 *
 * Note: This uses the fallback hardcoded config only.
 * Use getStandardSizeTableAsync() to fetch from WordPress.
 */
/** @internal Exposed for test teardown only. */
export function clearStandardSizesCache(): void {
  for (const key of Object.keys(standardSizesCache)) {
    delete standardSizesCache[key];
  }
}

export function getStandardSizeTable(locationCode: string): StandardSizeTable {
  // Return cached if available
  if (standardSizesCache[locationCode]) {
    return standardSizesCache[locationCode];
  }

  // Return hardcoded fallback
  const override = facilityStandardSizes[locationCode];
  if (!override) return defaultTable;

  return {
    units: override.units ?? defaultTable.units,
  };
}
